import { Lock } from "lucide-react";
import type { PolicyData } from "../PolicyShell";

export const PRIVACY_POLICY: PolicyData = {
  slug: "privacy-policy",
  title: "Privacy & Data Policy",
  subtitle: "",
  effectiveDate: "January 2026",
  icon: Lock,
  documentType: "Privacy Notice",
  version: "1.0",
  sections: [
    {
      id: "introduction",
      number: "1",
      title: "Introduction",
      blocks: [
        {
          type: "p",
          text: "This Privacy Policy explains how CashTap (“CashTap,” “Company,” “we,” “our,” or “us”) collects, uses, stores, shares, and protects personal information when users access or use the CashTap platform, applications, APIs, wallets, payment services, collection tools, stablecoin-enabled services, and related financial technology services (collectively, the “Services”).",
        },
        {
          type: "p",
          text: "By accessing or using CashTap, you acknowledge that you have read and understood this Privacy Policy.",
        },
      ],
    },
    {
      id: "scope",
      number: "2",
      title: "Scope of This Policy",
      blocks: [
        { type: "p", text: "This Privacy Policy applies to:" },
        {
          type: "ul",
          items: [
            "Individual users",
            "Business users",
            "Merchants",
            "API users",
            "Website visitors",
            "Mobile application users",
            "Wallet users",
            "Payment link users",
          ],
        },
        { type: "p", text: "This policy applies to information collected through:" },
        {
          type: "ul",
          items: [
            "Websites",
            "Mobile applications",
            "APIs",
            "Customer support",
            "Compliance procedures",
            "Third-party integrations",
          ],
        },
      ],
    },
    {
      id: "information-we-collect",
      number: "3",
      title: "Information We Collect",
      blocks: [
        {
          type: "p",
          text: "CashTap may collect the following categories of information.",
        },
      ],
    },
    {
      id: "personal-information",
      number: "3.1",
      title: "Personal Information",
      level: 2,
      blocks: [
        { type: "p", text: "Including:" },
        {
          type: "ul",
          items: [
            "Full legal name",
            "Date of birth",
            "Residential address",
            "Government-issued identification",
            "Email address",
            "Phone number",
            "Selfie/liveness verification data",
            "Beneficial ownership information",
          ],
        },
      ],
    },
    {
      id: "business-information",
      number: "3.2",
      title: "Business Information",
      level: 2,
      blocks: [
        { type: "p", text: "For business accounts:" },
        {
          type: "ul",
          items: [
            "Legal entity name",
            "Business registration documents",
            "EIN or tax identification number",
            "Authorized representative details",
            "Licensing information",
            "Business activity descriptions",
          ],
        },
      ],
    },
    {
      id: "financial-information",
      number: "3.3",
      title: "Financial Information",
      level: 2,
      blocks: [
        { type: "p", text: "Including:" },
        {
          type: "ul",
          items: [
            "Wallet addresses",
            "Payment details",
            "Transaction history",
            "Stablecoin transaction data",
            "Virtual account information",
            "Settlement information",
            "Blockchain transaction identifiers",
          ],
        },
        {
          type: "p",
          text: "CashTap does not store full payment card details unless processed through approved third-party payment processors.",
        },
      ],
    },
    {
      id: "device-technical",
      number: "3.4",
      title: "Device & Technical Information",
      level: 2,
      blocks: [
        { type: "p", text: "Including:" },
        {
          type: "ul",
          items: [
            "IP addresses",
            "Device identifiers",
            "Browser information",
            "Operating system information",
            "Session activity",
            "Login history",
            "Geolocation indicators",
            "Device fingerprinting information",
          ],
        },
      ],
    },
    {
      id: "usage-information",
      number: "3.5",
      title: "Usage Information",
      level: 2,
      blocks: [
        { type: "p", text: "Including:" },
        {
          type: "ul",
          items: [
            "Platform interactions",
            "Transaction activity",
            "Feature usage",
            "API usage",
            "Support interactions",
            "User preferences",
          ],
        },
      ],
    },
    {
      id: "how-we-use",
      number: "4",
      title: "How We Use Information",
      blocks: [
        {
          type: "p",
          text: "CashTap may use collected information for purposes including:",
        },
        {
          type: "ul",
          items: [
            "Providing Services",
            "Account verification",
            "Identity verification (KYC)",
            "Fraud prevention",
            "AML and sanctions compliance",
            "Transaction monitoring",
            "Customer support",
            "Risk management",
            "Security monitoring",
            "Regulatory reporting",
            "Platform improvement",
            "Analytics",
            "Service optimization",
            "Legal compliance",
            "Marketing communications",
            "Promotional campaigns",
          ],
        },
      ],
    },
    {
      id: "aml-monitoring",
      number: "5",
      title: "AML, Fraud & Compliance Monitoring",
      blocks: [
        { type: "p", text: "CashTap may use personal information to:" },
        {
          type: "ul",
          items: [
            "Conduct identity verification",
            "Monitor transactions",
            "Detect suspicious activity",
            "Screen sanctions exposure",
            "Conduct fraud prevention reviews",
            "Perform blockchain monitoring",
            "Investigate security incidents",
            "Comply with legal obligations",
          ],
        },
        {
          type: "p",
          text: "CashTap may retain compliance-related information as required by applicable laws and regulations.",
        },
      ],
    },
    {
      id: "information-sharing",
      number: "6",
      title: "Information Sharing",
      blocks: [
        { type: "p", text: "CashTap may share information with:" },
        {
          type: "ul",
          items: [
            "Banking partners",
            "Custodial providers",
            "Payment processors",
            "Compliance vendors",
            "Identity verification providers",
            "Blockchain analytics providers",
            "Infrastructure providers",
            "Regulatory authorities",
            "Law enforcement agencies",
          ],
        },
        { type: "p", text: "Information may also be shared:" },
        {
          type: "ul",
          items: [
            "To comply with legal obligations",
            "To protect platform security",
            "To prevent fraud or financial crime",
            "To enforce Terms of Use",
            "During corporate transactions or restructuring",
          ],
        },
      ],
    },
    {
      id: "third-party-providers",
      number: "7",
      title: "Third-Party Service Providers",
      blocks: [
        {
          type: "p",
          text: "CashTap relies on third-party service providers for certain Services.",
        },
        { type: "p", text: "These providers may include:" },
        {
          type: "ul",
          items: [
            "Banking infrastructure providers",
            "Wallet infrastructure providers",
            "Cloud hosting providers",
            "Analytics providers",
            "Compliance vendors",
            "Payment processors",
            "Security vendors",
          ],
        },
        {
          type: "p",
          text: "Third-party providers may process information on behalf of CashTap subject to contractual and legal protections.",
        },
      ],
    },
    {
      id: "blockchain-transparency",
      number: "8",
      title: "Blockchain & Digital Asset Transparency",
      blocks: [
        {
          type: "p",
          text: "Blockchain transactions may be publicly visible on distributed ledger networks.",
        },
        {
          type: "p",
          text: "Users acknowledge that blockchain networks may permanently record:",
        },
        {
          type: "ul",
          items: [
            "Wallet addresses",
            "Transaction hashes",
            "Transfer amounts",
            "Blockchain activity",
          ],
        },
        { type: "p", text: "CashTap does not control public blockchain visibility." },
      ],
    },
    {
      id: "cookies",
      number: "9",
      title: "Cookies & Tracking Technologies",
      blocks: [
        { type: "p", text: "CashTap may use:" },
        {
          type: "ul",
          items: [
            "Cookies",
            "Session technologies",
            "Device fingerprinting",
            "Analytics technologies",
            "Security monitoring tools",
          ],
        },
        { type: "p", text: "These technologies may be used for:" },
        {
          type: "ul",
          items: [
            "Security",
            "Fraud prevention",
            "Analytics",
            "Authentication",
            "Service optimization",
          ],
        },
        {
          type: "p",
          text: "Users may disable certain browser cookies, though some platform functionality may be impacted.",
        },
      ],
    },
    {
      id: "data-retention",
      number: "10",
      title: "Data Retention",
      blocks: [
        {
          type: "p",
          text: "CashTap may retain information for periods necessary to:",
        },
        {
          type: "ul",
          items: [
            "Provide Services",
            "Comply with legal obligations",
            "Satisfy AML/BSA requirements",
            "Resolve disputes",
            "Enforce agreements",
            "Maintain security and audit records",
          ],
        },
        {
          type: "p",
          text: "Retention periods may vary depending on regulatory and operational requirements.",
        },
      ],
    },
    {
      id: "security",
      number: "11",
      title: "Security Measures",
      blocks: [
        {
          type: "p",
          text: "CashTap maintains security measures designed to protect information including:",
        },
        {
          type: "ul",
          items: [
            "Encryption",
            "Access controls",
            "Secure infrastructure",
            "Monitoring systems",
            "Audit logging",
            "Risk detection systems",
            "Incident response procedures",
          ],
        },
        { type: "p", text: "However, no system can guarantee absolute security." },
      ],
    },
    {
      id: "user-responsibilities",
      number: "12",
      title: "User Responsibilities & Account Security",
      blocks: [
        {
          type: "p",
          text: "Users and business accounts are solely responsible for maintaining the confidentiality and security of their:",
        },
        {
          type: "ul",
          items: [
            "Login credentials",
            "Passwords",
            "One-time verification codes",
            "Recovery phrases",
            "Wallet credentials",
            "API keys",
            "Devices and account access information",
          ],
        },
        {
          type: "p",
          text: "Users and businesses agree not to share account credentials with unauthorized persons.",
        },
        {
          type: "p",
          text: "CashTap shall not be responsible or liable for losses, unauthorized access, fraudulent activity, account compromise, or damages resulting from:",
        },
        {
          type: "ul",
          items: [
            "Shared credentials",
            "Weak passwords",
            "User negligence",
            "Phishing attacks",
            "Malware infections",
            "Unauthorized device access",
            "Failure to maintain account security",
          ],
        },
        {
          type: "p",
          text: "Users and businesses are responsible for immediately notifying CashTap if unauthorized access or suspicious activity is detected.",
        },
        { type: "p", text: "Users are additionally responsible for:" },
        {
          type: "ul",
          items: [
            "Maintaining device security",
            "Using secure internet connections",
            "Monitoring account activity",
            "Protecting wallet credentials and recovery phrases",
          ],
        },
      ],
    },
    {
      id: "international-transfers",
      number: "13",
      title: "International Data Transfers",
      blocks: [
        {
          type: "p",
          text: "Information may be processed or stored in jurisdictions outside the user’s country of residence.",
        },
        {
          type: "p",
          text: "By using CashTap, users acknowledge and consent to cross-border data transfers where permitted by law.",
        },
      ],
    },
    {
      id: "sanctions-screening",
      number: "14",
      title: "Sanctions & Compliance Screening",
      blocks: [
        { type: "p", text: "CashTap may process personal information for:" },
        {
          type: "ul",
          items: [
            "OFAC screening",
            "AML monitoring",
            "Fraud investigations",
            "Regulatory reporting",
            "Compliance reviews",
          ],
        },
        {
          type: "p",
          text: "Users may be restricted or denied access based on compliance obligations.",
        },
      ],
    },
    {
      id: "childrens-privacy",
      number: "15",
      title: "Children's Privacy",
      blocks: [
        {
          type: "p",
          text: "CashTap Services are not intended for individuals under 18 years of age.",
        },
        {
          type: "p",
          text: "CashTap does not knowingly collect personal information from minors.",
        },
      ],
    },
    {
      id: "user-rights",
      number: "16",
      title: "User Rights",
      blocks: [
        {
          type: "p",
          text: "Depending on applicable laws, users may have rights including:",
        },
        {
          type: "ul",
          items: [
            "Access to personal information",
            "Correction of inaccurate information",
            "Deletion requests",
            "Restriction of processing",
            "Objection to certain processing",
            "Data portability requests",
          ],
        },
        {
          type: "p",
          text: "CashTap may decline requests where retention is required by law or regulatory obligations.",
        },
      ],
    },
    {
      id: "marketing",
      number: "17",
      title: "Marketing Communications & Email Collection",
      blocks: [
        {
          type: "p",
          text: "CashTap may collect and process email addresses and contact information for purposes including:",
        },
        {
          type: "ul",
          items: [
            "Account notifications",
            "Security alerts",
            "Compliance communications",
            "Product announcements",
            "Promotional campaigns",
            "Marketing communications",
            "Service updates",
            "Feature announcements",
            "Customer support communications",
          ],
        },
        {
          type: "p",
          text: "By using CashTap Services, users consent to receiving electronic communications from CashTap where permitted by applicable law.",
        },
        {
          type: "p",
          text: "Users may opt out of certain promotional or marketing communications through available unsubscribe mechanisms; however, operational, compliance, legal, and security-related communications may still be sent where necessary.",
        },
      ],
    },
    {
      id: "third-party-links",
      number: "18",
      title: "Third-Party Links & Services",
      blocks: [
        {
          type: "p",
          text: "CashTap may contain links or integrations to third-party websites or services.",
        },
        {
          type: "p",
          text: "CashTap is not responsible for the privacy practices or content of third-party services.",
        },
        { type: "p", text: "Users access third-party services at their own risk." },
      ],
    },
    {
      id: "no-guarantee",
      number: "19",
      title: "No Guarantee of Absolute Security",
      blocks: [
        {
          type: "p",
          text: "While CashTap implements security controls, users acknowledge that:",
        },
        {
          type: "ul",
          items: [
            "Internet transmissions are not fully secure",
            "Blockchain systems may involve technical risks",
            "Third-party providers may experience failures",
            "Unauthorized access may occur despite safeguards",
          ],
        },
        {
          type: "p",
          text: "CashTap does not guarantee absolute protection against cyber threats or unauthorized access.",
        },
      ],
    },
    {
      id: "law-enforcement",
      number: "20",
      title: "Law Enforcement & Regulatory Cooperation",
      blocks: [
        { type: "p", text: "CashTap may disclose information to:" },
        {
          type: "ul",
          items: [
            "Regulatory authorities",
            "Law enforcement agencies",
            "Courts",
            "Government authorities",
            "Compliance partners",
          ],
        },
        { type: "p", text: "Where required by law or necessary to:" },
        {
          type: "ul",
          items: [
            "Prevent fraud",
            "Protect platform security",
            "Enforce legal rights",
            "Comply with legal obligations",
          ],
        },
      ],
    },
    {
      id: "business-transfers",
      number: "21",
      title: "Business Transfers",
      blocks: [
        { type: "p", text: "If CashTap or Aixona LLC undergoes:" },
        {
          type: "ul",
          items: [
            "Merger",
            "Acquisition",
            "Asset sale",
            "Corporate restructuring",
          ],
        },
        {
          type: "p",
          text: "User information may be transferred as part of the transaction subject to applicable laws.",
        },
      ],
    },
    {
      id: "updates",
      number: "22",
      title: "Updates to This Privacy Policy",
      blocks: [
        { type: "p", text: "CashTap may modify this Privacy Policy periodically." },
        {
          type: "p",
          text: "Updated versions become effective upon publication unless otherwise stated.",
        },
        {
          type: "p",
          text: "Continued use of Services constitutes acceptance of revised policies.",
        },
      ],
    },
    {
      id: "contact",
      number: "23",
      title: "Contact Information",
      blocks: [
        { type: "p", text: "For privacy, compliance, or legal inquiries:" },
        { type: "h3", text: "CashTap Privacy & Compliance Team" },
        { type: "p", text: "" },
        {
          type: "p",
          text: "Official communication channels may be provided through the CashTap platform.",
        },
      ],
    },
    {
      id: "final-acknowledgment",
      number: "24",
      title: "Final Acknowledgment",
      blocks: [
        { type: "p", text: "By using CashTap, users acknowledge and agree that:" },
        {
          type: "ul",
          items: [
            "Their information may be processed for compliance and security purposes",
            "Blockchain transactions may be publicly visible",
            "Third-party providers may process information on behalf of CashTap",
            "CashTap may retain information as required by law",
            "Certain information sharing may occur for regulatory and fraud prevention purposes",
            "Users and businesses are responsible for maintaining the confidentiality of their account credentials and login information",
          ],
        },
      ],
    },
  ],
  footerNote: "Version 1.0 — January 2026. Initial Privacy Policy.",
};
